import React, { useCallback, useMemo } from "react";

import { faFolderOpen, faLink } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tag from "antd/es/tag";
import cn from "classnames";

import Resource from "@mapmycustomers/shared/types/entity/Resource";
import { isDefined, isNotEmpty } from "@mapmycustomers/shared/util/assert";
import { stopEventPropagation } from "@mapmycustomers/shared/util/browser";
import formatFileSize from "@mapmycustomers/shared/util/formatters/formatFileSize";
import { fileTypeToIcons, getFileType } from "@mapmycustomers/ui";

import RadioButton from "@app/component/input/RadioButton";
import { formatRawDate } from "@app/util/formatters";

import Checkbox from "../input/Checkbox";

import styles from "./ResourcesSelectModal.module.scss";

interface ResourceItemProps {
  multiselect?: boolean;
  onChangeSelect: (resource: Resource) => void;
  resource: Resource;
  selectedResources: Resource[];
}

const ResourceItem = ({
  multiselect,
  onChangeSelect,
  resource,
  selectedResources,
}: ResourceItemProps) => {
  const { file, folder, name, updatedAt } = resource;

  const selected = useMemo(
    () => selectedResources.some(({ id }) => id === resource.id),
    [resource.id, selectedResources]
  );

  const handleChange = useCallback(() => {
    onChangeSelect(resource);
  }, [onChangeSelect, resource]);

  return (
    <div
      className={cn(styles.resourceItem, { [styles.selected]: selected })}
      onClick={handleChange}
    >
      <div className={styles.resource}>
        {multiselect ? (
          <Checkbox checked={selected} onChange={handleChange} onClick={stopEventPropagation} />
        ) : (
          <RadioButton checked={selected} onChange={handleChange} onClick={stopEventPropagation} />
        )}
        <div className={styles.resourceInfo}>
          <FontAwesomeIcon
            className={styles.fileIcon}
            icon={file ? fileTypeToIcons[getFileType(file)].solid : faLink}
          />
          <div className={styles.resourceText}>
            <span className={styles.resourceName}>{name}</span>
            <span className={styles.resourceMeta}>
              {[
                formatRawDate(updatedAt, "PP"),
                isNotEmpty(file?.contentLength)
                  ? formatFileSize(file.contentLength, 1000)
                  : undefined,
              ]
                .filter(isDefined)
                .join(" – ")}
            </span>
          </div>
        </div>
      </div>
      {folder && (
        <Tag className={styles.tag}>
          <FontAwesomeIcon className={styles.tagIcon} icon={faFolderOpen} />
          <span className={styles.folderName}>{folder.name}</span>
        </Tag>
      )}
    </div>
  );
};

export default ResourceItem;
